import { useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles, UMInput, UMR, UMSelect } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { planStatus, staleReasons } from './model';
import { PlanBricks } from './PlanBricks';
import { updateBuildingIds } from './composition';
import { aggregate, type CalculationSection } from './sectionEngine';
import type { Family, Plan, PlanBrick, Project } from './types';
import { SaveBar, StatusPill } from './ui';

interface Props {
  project: Project; geo?: PlanGeoData; canWrite: boolean; selectedId: string;
  active: PlanBrick; onBrick: (brick: PlanBrick) => void;
  onSelect: (id: string) => void;
  onCreate: (name: string, energy: Plan['energy']) => string;
  onDuplicate: (id: string) => string;
  onUpdate: (id: string, changes: Partial<Plan>) => void;
  onRun: (id: string, section: CalculationSection) => void;
  onOpenSet: (family: Family, id: string) => void;
  onSST: () => void; onExport: () => void;
  draftPlan: Plan | null; onDraftChange: (plan: Plan | null) => void;
}

export const PLAN_STEPS: { id: PlanBrick; label: string }[] = [
  { id: 'buildings', label: 'Bâtiments' }, { id: 'trace', label: 'Tracé' },
  { id: 'phasing', label: 'Phasage' }, { id: 'recap', label: 'Récapitulatif' },
];

export function PlanDevPanel(p: Props) {
  const [name, setName] = useState('');
  const [energy, setEnergy] = useState<Plan['energy']>('chaud');
  const [collapsed, setCollapsed] = useState(false);
  const saved = p.project.plans.find((item) => item.id === p.selectedId);
  const plan = p.draftPlan?.id === saved?.id ? p.draftPlan : saved;
  const dirty = !!saved && !!p.draftPlan && JSON.stringify(saved) !== JSON.stringify(p.draftPlan);
  const running = plan?.status === 'running';
  const confirm = () => {
    if (!plan) return;
    p.onUpdate(plan.id, { ...plan, buildingsConfirmed: p.active === 'buildings' ? true : plan.buildingsConfirmed });
    p.onDraftChange(null);
  };
  return <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
    {!collapsed && <aside style={{ flex: '0 0 245px', overflowY: 'auto', borderRight: `1px solid ${UMR.border}`, padding: 14, background: UMR.white }}>
      <NJText as="h2" scale="lg">Plans de développement</NJText>
      <NJButton label="Replier les plans" variant="secondary" emphasis="minimal" scale="sm" onClick={() => setCollapsed(true)} />
      {p.canWrite && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '12px 0' }}>
        <UMFormStyles />
        <UMInput value={name} ariaLabel="Nom du plan" placeholder="Nom du nouveau plan" onChange={setName} />
        <UMSelect value={energy} ariaLabel="Énergie du plan" onChange={(v) => setEnergy(v as Plan['energy'])}
          options={[{ value: 'chaud', label: 'Chaud' }, { value: 'froid', label: 'Froid' }]} />
        <NJButton label="Créer un plan" scale="sm" disabled={!name.trim() || dirty || running} onClick={() => {
          p.onSelect(p.onCreate(name.trim(), energy)); setName(''); p.onBrick('buildings');
        }} />
      </div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {p.project.plans.map((item) => <div key={item.id} style={{ border: `1px solid ${UMR.border}`, padding: 8 }}>
          <NJText scale="sm" style={{ overflowWrap: 'anywhere', margin: '0 0 4px' }}>{item.name}</NJText>
          <NJButton label={item.id === plan?.id ? 'Plan actif' : 'Choisir ce plan'} variant={item.id === plan?.id ? 'primary' : 'secondary'} scale="sm"
            disabled={running} onClick={() => p.onSelect(item.id)} />
          <NJText scale="xs">{item.energy === 'chaud' ? 'Chaud' : 'Froid'} · <StatusPill status={planStatus(p.project, item)} /></NJText>
        </div>)}
      </div>
    </aside>}
    {plan ? <div style={{ display: 'flex', flex: 1, minWidth: 0, flexDirection: 'column' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: `1px solid ${UMR.border}` }}>
        {collapsed && <NJButton label="Afficher les plans" variant="secondary" scale="sm" onClick={() => setCollapsed(false)} />}
        <NJText as="h3" scale="md" style={{ margin: 0 }}>{plan.name}</NJText>
        <StatusPill status={planStatus(p.project, plan)} />
        {p.canWrite && <NJButton label="Dupliquer" variant="secondary" scale="sm" disabled={dirty || running} onClick={() => p.onSelect(p.onDuplicate(plan.id))} />}
      </header>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: 8, borderBottom: `1px solid ${UMR.border}` }}>
        <nav aria-label="Étapes du plan" style={{ display: 'flex', gap: 4, flex: 1 }}>
          {PLAN_STEPS.map((step, index) => <NJButton key={step.id} label={`${index + 1}. ${step.label}`} scale="sm"
            variant={p.active === step.id ? 'primary' : 'secondary'} onClick={() => p.onBrick(step.id)} />)}
        </nav>
        {p.canWrite && <SaveBar dirty={dirty || (p.active === 'buildings' && !plan.buildingsConfirmed)}
          readOnly={running} onCancel={() => p.onDraftChange(null)} onSave={confirm} />}
      </div>
      {running && <NJText scale="xs" role="status" style={{ margin: 6 }}>En cours · {plan.calculationSection === 'density' ? 'Densité' : plan.calculationSection === 'phasing' ? 'Phasage' : 'Tracé'} · simulation locale</NJText>}
      {plan.calculationError && <NJText scale="xs" role="alert" style={{ margin: 6 }}>En échec : {plan.calculationError}</NJText>}
      {staleReasons(p.project, plan).length > 0 && <div style={{ padding: '6px 14px', background: UMR.grey100, maxHeight: 86, overflowY: 'auto' }}>
        {staleReasons(p.project, plan).map((reason) => <NJText key={reason} scale="xs" style={{ margin: 0 }}>{reason}</NJText>)}
      </div>}
      <PlanBricks project={p.project} plan={plan} geo={p.geo} canWrite={p.canWrite && !running} dirty={dirty} active={p.active}
        onOpenSet={p.onOpenSet} onSST={p.onSST} onExport={p.onExport}
        onRun={(section) => p.onRun(plan.id, section)}
        onUpdate={(changes) => {
          const next = aggregate(p.project, {
            ...plan, ...changes,
            ...(changes.buildingIds ? { buildingsConfirmed: false } : {}),
            ...(changes.buildingIds && !('selectionIds' in changes) ? updateBuildingIds(p.project, plan, changes.buildingIds) : {}),
          }, p.geo);
          p.onDraftChange(saved && JSON.stringify(saved) === JSON.stringify(next) ? null : next);
        }} />
    </div> : <NJText style={{ padding: 24 }}>Aucun plan. Créez un plan pour commencer.</NJText>}
  </div>;
}
